import { getPrivacySnapshot } from './privacyMode';

/**
 * BRO-1567: answers the service worker when it asks whether privacy mode is on.
 *
 * THE HOLE THIS CLOSES. Every inbound message pops an OS notification built by the queue job
 * `SendWhatsappMessagePushNotification`, carrying "From: {full_name}" and the message text.
 * That notification appears on the operator's desktop, which is the thing being shared. It is
 * the one surface where masking the application changes nothing at all.
 *
 * A previous attempt masked `NotificationService.js` instead. That file's `init()` is
 * commented out, so the fix landed in code that never runs. See the notice at the top of it.
 *
 * WHY THE SERVICE WORKER ASKS THE PAGE rather than the server deciding:
 *
 * - The job runs with no session, so it cannot read the flag at all. Masking server-side
 *   would also mean a masked value existing outside a view, which the export guardrail
 *   exists to prevent.
 * - The flag stays in one place. No second copy for the service worker to read stale.
 * - It matches the threat model exactly. A demonstration means a visible page. No open
 *   window means nobody is watching the app, so the normal notification is correct rather
 *   than a gap.
 *
 * REGISTERED ON EVERY PAGE, not inside `PushNotificationService.initialize()`. Any open
 * window must be able to answer, and initialize() only runs once push has been set up.
 */
export function registerPrivacyServiceWorkerBridge(): void {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        return;
    }

    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        if (event.data?.type !== 'broadcaster:is-privacy-mode-on') {
            return;
        }

        // The worker sends a MessagePort and waits a couple of hundred milliseconds.
        //
        // BRO-1567 (S1): THIS COMMENT USED TO SAY that replying on anything else, or not
        // replying, "means it shows the notification as it does now". THAT WAS WRONG, and
        // wrong in the dangerous direction. The worker treats silence as MASK: a window that
        // is open and does not answer is one it could not ask, which is not the same as one
        // that said no. Not replying MASKS the notification.
        const port = event.ports?.[0];

        if (!port) {
            return;
        }

        /**
         * ONLY A PAGE THAT CAN KNOW MAY ANSWER, AND "CAN KNOW" MEANS THE LAYOUT SEEDED IT.
         *
         * BRO-1567 (F1), found in the browser by the acceptance run. This used to test
         * `window.BroadcasterPrivacy`, and that guard NEVER HELD: privacyMode.ts's
         * globalState() manufactures that object on any page where the module loads, and
         * `app.js` loads on the guest, onboarding and marketing layouts too. So a sign-in,
         * 404 or marketing tab answered "privacy is off" with the full authority of a real
         * reply, and with privacy ON, a notification arrived showing a real contact's name
         * and message text on the screen being shared.
         *
         * The seed is written only by the dashboard layout, and nothing in the module ever
         * creates it - readSeed() reads and returns `{}` when absent, and the one mutation
         * is guarded by `if (seed)`. So its presence is a fact about the RENDER rather than
         * about the bundle, which is the property this guard needs.
         *
         * A page without it stays SILENT rather than answering false, so the worker's
         * timeout decides instead of being overruled by a page that does not know. Saying
         * nothing and saying no are different answers, and only one of them is honest here.
         *
         * The test that "proved" the old guard deleted the manufactured global first - a
         * state no real page reaches. See privacyServiceWorkerBridge.test.ts.
         */
        const seeded = (window as unknown as { BroadcasterPrivacySeed?: unknown }).BroadcasterPrivacySeed;

        if (!seeded) {
            return;
        }

        try {
            port.postMessage({ enabled: getPrivacySnapshot().enabled === true });
        } catch (error) {
            // A closed port is not worth breaking the page over: the worker's timeout
            // already covers "no answer", and that path shows the normal notification.
            console.error('Could not answer the service worker privacy query', error);
        }
    });
}
